import * as THREE from 'three';
import { Entity } from './Entity.js';
import type { Circle } from '../systems/Collision.js';
import type { Room } from '../level/Room.js';
import type { ProjectileModifiers } from '../parts/Part.js';

/**
 * Configuration for creating projectiles.
 */
export interface ProjectileConfig {
  /** Stacking modifiers (new system) */
  modifiers?: ProjectileModifiers;

  /** Visual arc height for lobbed projectiles */
  arcHeight?: number;

  /** Projectile color */
  color?: number;
}

/**
 * Callback type for spawning split projectiles.
 * Returns the spawned projectile so parent modifiers can be inherited.
 */
export type SplitCallback = (
  x: number,
  y: number,
  angle: number,
  damage: number,
  parentModifiers?: ProjectileModifiers
) => Projectile | null;

/**
 * Callback type for finding homing targets.
 */
export type FindTargetCallback = (x: number, y: number, owner: Entity | null) => Entity | null;

/**
 * Callback type for finding multiple targets in range (for chain/explosion).
 */
export type FindTargetsInRangeCallback = (
  x: number,
  y: number,
  radius: number,
  owner: Entity | null,
  exclude?: Set<Entity>
) => Entity[];

/**
 * Callback type for dealing damage to an entity.
 */
export type DealDamageCallback = (target: Entity, damage: number) => void;

/**
 * Callback for spawning visual effects.
 */
export type SpawnEffectCallback = (x: number, y: number, type: 'explosion' | 'chain', radius?: number) => void;

/**
 * Projectile entity with stackable modifier system.
 *
 * Modifiers can be combined for emergent effects:
 * - homing + bouncing = "Pinball" - bounces toward enemies
 * - homing + splitting = "Swarm" - children home independently
 * - piercing + splitting = "Hydra" - splits when pierce exhausts
 * - explosive + chain = damage spreads in AOE then chains
 *
 * Max 3 modifiers per projectile. Each modifier: -15% damage.
 */
export class Projectile extends Entity {
  private velocity: { x: number; y: number };
  private lifetime: number;
  private maxLifetime: number;
  private baseDamage: number;
  private speed: number;
  private angle: number;
  private color: number;

  // Modifier system
  private modifiers: ProjectileModifiers;

  // Modifier state
  private bounceCount: number = 0;
  private pierceRemaining: number = 0;
  private hasSplit: boolean = false;
  private hitEntities: Set<Entity> = new Set();

  // Visual arc (for mortar-style weapons)
  private arcHeight: number;
  private startPosition: { x: number; y: number };

  // Collision
  private readonly collisionRadius: number = 0.1;
  private owner: Entity | null = null;
  private room: Room | null = null;

  // Callbacks
  private onDestroyCallback: ((x: number, y: number) => void) | null = null;
  private findTargetCallback: FindTargetCallback | null = null;
  private splitCallback: SplitCallback | null = null;
  private findTargetsInRangeCallback: FindTargetsInRangeCallback | null = null;
  private dealDamageCallback: DealDamageCallback | null = null;
  private spawnEffectCallback: SpawnEffectCallback | null = null;

  constructor(
    x: number,
    y: number,
    angle: number,
    speed: number,
    damage: number,
    lifetime: number,
    config: ProjectileConfig = {}
  ) {
    super(x, y, angle);

    this.speed = speed;
    this.baseDamage = damage;
    this.maxLifetime = lifetime;
    this.lifetime = lifetime;
    this.angle = angle;
    this.color = config.color || 0xffeb3b;

    // Store modifiers
    this.modifiers = config.modifiers || {};

    // Initialize modifier state
    if (this.modifiers.piercing) {
      this.pierceRemaining = this.modifiers.piercing.maxPierces;
    }

    // Visual arc
    this.arcHeight = config.arcHeight || 0;
    this.startPosition = { x, y };

    // Calculate velocity
    this.velocity = {
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed,
    };

    this.createMesh();
  }

  /**
   * Get effective damage after modifier penalties.
   * Each modifier beyond the first reduces damage by 15%.
   */
  getDamage(): number {
    const modifierCount = this.getModifierCount();
    const penalty = Math.max(0, modifierCount - 1) * 0.15;
    return this.baseDamage * (1 - penalty);
  }

  /**
   * Count active modifiers.
   */
  getModifierCount(): number {
    let count = 0;
    if (this.modifiers.homing) count++;
    if (this.modifiers.bouncing) count++;
    if (this.modifiers.piercing) count++;
    if (this.modifiers.splitting) count++;
    if (this.modifiers.explosive) count++;
    if (this.modifiers.chain) count++;
    return count;
  }

  /**
   * Check if a modifier is active.
   */
  hasModifier(type: keyof ProjectileModifiers): boolean {
    return this.modifiers[type] !== undefined;
  }

  /**
   * Get the modifiers (for inheritance by split projectiles).
   */
  getModifiers(): ProjectileModifiers {
    return this.modifiers;
  }

  private createMesh(): void {
    const geometry = new THREE.SphereGeometry(0.1, 8, 8);
    geometry.scale(1, 1, 2);

    const material = new THREE.MeshStandardMaterial({
      color: this.color,
      emissive: this.color,
      emissiveIntensity: 0.5,
      metalness: 0.3,
      roughness: 0.4,
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.updateMeshTransform();
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // === HOMING ===
    if (this.modifiers.homing) {
      this.updateHoming(dtSec);
    }

    // === MOVEMENT ===
    this.position.x += this.velocity.x * dtSec;
    this.position.y += this.velocity.y * dtSec;

    // === VISUAL ARC ===
    if (this.arcHeight > 0 && this.mesh) {
      const t = this.lifetime / this.maxLifetime;
      const height = 4 * this.arcHeight * t * (1 - t);
      this.mesh.position.z = height;
    }

    // === WALL COLLISION ===
    if (this.room) {
      const hitWall = this.checkWallCollision();
      if (hitWall) {
        if (this.modifiers.bouncing && this.bounceCount < this.modifiers.bouncing.maxBounces) {
          // Bounce handled in checkWallCollision
        } else {
          // Hit wall without bounce - trigger effects and destroy
          if (this.modifiers.explosive) {
            this.triggerExplosion();
          }
          this.destroyWithEffect();
          return;
        }
      }
    }

    // === LIFETIME ===
    this.lifetime -= dtSec;

    // === SPLITTING (time-based for projectiles that haven't hit anything) ===
    if (this.modifiers.splitting && !this.hasSplit && this.lifetime < this.maxLifetime * 0.5) {
      this.triggerSplit();
      return;
    }

    // === EXPIRED ===
    if (this.lifetime <= 0) {
      if (this.modifiers.explosive) {
        this.triggerExplosion();
      }
      this.destroyWithEffect();
      return;
    }

    this.updateMeshTransform();
  }

  /**
   * Check and handle wall collision.
   * Returns true if wall was hit.
   */
  private checkWallCollision(): boolean {
    if (!this.room) return false;

    const halfWidth = this.room.width / 2;
    const halfHeight = this.room.height / 2;

    let hitWallX = false;
    let hitWallY = false;

    if (this.position.x - this.collisionRadius < -halfWidth) {
      hitWallX = true;
      this.position.x = -halfWidth + this.collisionRadius;
    } else if (this.position.x + this.collisionRadius > halfWidth) {
      hitWallX = true;
      this.position.x = halfWidth - this.collisionRadius;
    }

    if (this.position.y - this.collisionRadius < -halfHeight) {
      hitWallY = true;
      this.position.y = -halfHeight + this.collisionRadius;
    } else if (this.position.y + this.collisionRadius > halfHeight) {
      hitWallY = true;
      this.position.y = halfHeight - this.collisionRadius;
    }

    if (hitWallX || hitWallY) {
      if (this.modifiers.bouncing && this.bounceCount < this.modifiers.bouncing.maxBounces) {
        if (hitWallX) this.velocity.x *= -1;
        if (hitWallY) this.velocity.y *= -1;
        this.bounceCount++;
        this.angle = Math.atan2(this.velocity.y, this.velocity.x);
        this.rotation = this.angle;
        return false; // Bounced, not destroyed
      }
      return true; // Hit wall, should be destroyed
    }

    return false;
  }

  private updateHoming(dtSec: number): void {
    if (!this.findTargetCallback) return;

    const target = this.findTargetCallback(this.position.x, this.position.y, this.owner);
    if (!target) return;

    const dx = target.position.x - this.position.x;
    const dy = target.position.y - this.position.y;
    const targetAngle = Math.atan2(dy, dx);

    let angleDiff = targetAngle - this.angle;
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

    const strength = this.modifiers.homing?.strength || 3;
    const maxTurn = strength * dtSec;
    const turn = Math.max(-maxTurn, Math.min(maxTurn, angleDiff));
    this.angle += turn;
    this.rotation = this.angle;

    this.velocity.x = Math.cos(this.angle) * this.speed;
    this.velocity.y = Math.sin(this.angle) * this.speed;
  }

  /**
   * Handle collision with an entity.
   * Called by Game.processCollisions().
   *
   * @returns true if projectile should be destroyed
   */
  onHit(target: Entity): boolean {
    // Skip if we already hit this entity (for piercing)
    if (this.hitEntities.has(target)) {
      return false;
    }

    this.hitEntities.add(target);

    // === CHAIN: Damage jumps to nearby enemies ===
    if (this.modifiers.chain) {
      this.triggerChain(target);
    }

    // === EXPLOSIVE: AOE damage ===
    if (this.modifiers.explosive) {
      this.triggerExplosion();
    }

    // === SPLITTING: Spawn child projectiles ===
    if (this.modifiers.splitting && !this.hasSplit) {
      this.triggerSplit();
      return true; // Parent destroyed after split
    }

    // === PIERCING: Pass through enemies ===
    if (this.modifiers.piercing) {
      this.pierceRemaining--;
      if (this.pierceRemaining > 0) {
        return false; // Keep going
      }
      // Pierce exhausted - could trigger split here for "Hydra" effect
      if (this.modifiers.splitting && !this.hasSplit) {
        this.triggerSplit();
      }
      return true;
    }

    // Default: destroy on hit
    return true;
  }

  /**
   * Check if this projectile has already hit the given entity.
   */
  hasHit(entity: Entity): boolean {
    return this.hitEntities.has(entity);
  }

  private triggerSplit(): void {
    if (this.hasSplit || !this.splitCallback) return;
    this.hasSplit = true;

    const count = this.modifiers.splitting?.count || 3;
    const inheritMods = this.modifiers.splitting?.inheritModifiers ?? true;

    const spreadAngle = Math.PI / 3; // 60 degrees spread
    for (let i = 0; i < count; i++) {
      const angleOffset = (i - (count - 1) / 2) * (spreadAngle / Math.max(count - 1, 1));
      const splitAngle = this.angle + angleOffset;
      const childDamage = this.baseDamage * 0.5;

      // Inherit modifiers (except splitting to prevent infinite recursion)
      let childModifiers: ProjectileModifiers | undefined;
      if (inheritMods) {
        childModifiers = { ...this.modifiers };
        delete childModifiers.splitting; // Don't inherit splitting
      }

      this.splitCallback(this.position.x, this.position.y, splitAngle, childDamage, childModifiers);
    }

    this.destroy();
  }

  private triggerExplosion(): void {
    if (!this.findTargetsInRangeCallback || !this.dealDamageCallback) return;

    const radius = this.modifiers.explosive?.radius || 2;
    const falloff = this.modifiers.explosive?.falloff || 0.5;

    // Spawn visual effect
    if (this.spawnEffectCallback) {
      this.spawnEffectCallback(this.position.x, this.position.y, 'explosion', radius);
    }

    const targets = this.findTargetsInRangeCallback(
      this.position.x,
      this.position.y,
      radius,
      this.owner,
      this.hitEntities // Don't double-hit the primary target
    );

    for (const target of targets) {
      const dx = target.position.x - this.position.x;
      const dy = target.position.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const damageMultiplier = 1 - (dist / radius) * (1 - falloff);
      this.dealDamageCallback(target, this.getDamage() * damageMultiplier);
    }
  }

  private triggerChain(initialTarget: Entity): void {
    if (!this.findTargetsInRangeCallback || !this.dealDamageCallback) return;

    const maxTargets = this.modifiers.chain?.targets || 3;
    const range = this.modifiers.chain?.range || 3;
    const falloff = this.modifiers.chain?.falloff || 0.8;

    const chained = new Set<Entity>([initialTarget]);
    let currentPos = { x: initialTarget.position.x, y: initialTarget.position.y };
    let remainingChains = maxTargets;
    let chainDamage = this.getDamage() * 0.7;

    // Spawn chain effect from projectile to first target
    if (this.spawnEffectCallback) {
      this.spawnEffectCallback(initialTarget.position.x, initialTarget.position.y, 'chain');
    }

    while (remainingChains > 0) {
      const targets = this.findTargetsInRangeCallback(
        currentPos.x,
        currentPos.y,
        range,
        this.owner,
        chained
      );

      if (targets.length === 0) break;

      // Find closest
      let closest: Entity | null = null;
      let closestDist = Infinity;
      for (const target of targets) {
        const dx = target.position.x - currentPos.x;
        const dy = target.position.y - currentPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < closestDist) {
          closestDist = dist;
          closest = target;
        }
      }

      if (!closest) break;

      this.dealDamageCallback(closest, chainDamage);
      chained.add(closest);

      // Spawn chain effect
      if (this.spawnEffectCallback) {
        this.spawnEffectCallback(closest.position.x, closest.position.y, 'chain');
      }

      currentPos = { x: closest.position.x, y: closest.position.y };
      remainingChains--;
      chainDamage *= falloff;
    }
  }

  private destroyWithEffect(): void {
    if (this.onDestroyCallback) {
      this.onDestroyCallback(this.position.x, this.position.y);
    }
    this.destroy();
  }

  // === GETTERS ===

  getLifetimeFraction(): number {
    return this.lifetime / this.maxLifetime;
  }

  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }

  getOwner(): Entity | null {
    return this.owner;
  }

  // === SETTERS ===

  setOwner(owner: Entity): void {
    this.owner = owner;
  }

  setRoom(room: Room): void {
    this.room = room;
  }

  setOnDestroy(callback: (x: number, y: number) => void): void {
    this.onDestroyCallback = callback;
  }

  setFindTarget(callback: FindTargetCallback): void {
    this.findTargetCallback = callback;
  }

  setSplitCallback(callback: SplitCallback): void {
    this.splitCallback = callback;
  }

  setFindTargetsInRange(callback: FindTargetsInRangeCallback): void {
    this.findTargetsInRangeCallback = callback;
  }

  setDealDamage(callback: DealDamageCallback): void {
    this.dealDamageCallback = callback;
  }

  setSpawnEffect(callback: SpawnEffectCallback): void {
    this.spawnEffectCallback = callback;
  }
}
